import React from "react";
import {
  BlueBirdTheme, Button, CrossIcon, DatePicker, Divider, Form, TextArea, Typography,
  useSnackbar,
} from "@kitbird/core";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { CardContent, PageCard, PageWrapper } from "../../../components/Content/Content.style";
import { HeaderText } from "../../../components/FloatHeader/FloatHeader.style";
import logo from "../../../assets/icons/logogram_bb.png";
import { ReactComponent as PaperListIcon } from "../../../assets/icons/paper_list.svg";
import Status from "../../../components/Status";
import SearchInput from "../../../components/SearchInput";
import FilterDropdown from "../../../components/FilterDropdown";
import { IOptionItem } from "../../../typings/common";
import {
  getDateIfValid, toNumber, toRupiah, toStringDate,
} from "../../../utils/common";
import CircledIcon from "../../../components/CircledIcon";
import { ReactComponent as MoneyIcon } from "../../../assets/icons/money.svg";
import { CommonTrxTypes } from "../../../enums/common";
import { Provider, useWithdrawalReceipt } from "./hooks/Provider";
import ModalReceiptDetail from "./components/ModalReceiptDetail";
import WithdrawalReceiptHistory from "./components/WithdrawalReceiptHistory";
import { ActionSection } from "../../../components/Filter/Filter.style";
import useAuth from "../../../components/Authentication/useAuth";
import WithdrawalTable from "./components/WithdrawalTable";
import { FormGroup, FormWrapper } from "../../../components/Form/Form.style";
import PageLoading from "../../../components/Loading/PageLoading";
import ModalFilters from "./components/ModalFilters";
import ModalWithdrawConfirm from "./components/ModalWithdrawConfirm";

const {
  blueGray, gray, primary, white,
} = BlueBirdTheme.colors;

const GridWrapper = styled.div<{ $show: boolean }>`
  display: grid;
  grid-template-columns: ${({ $show }) => ($show ? "calc(100% - 560px - 20px) 560px" : "calc(100%) 0")};
  gap: 20px;
  transition: all .5s;
  > div:nth-child(2) {
    display: ${({ $show }) => ($show ? "block" : "none")};
  }
`;

const CustomBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  padding: 12px;
  border-radius: 8px;
  background: ${primary[200]};
`;

const InputWrapper = styled.div`
  display: grid;
  gap: 12px;
  background: ${blueGray.background};
  border-radius: 8px;
  padding: 12px;
`;

const Wrap = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  font-weight: 500;
  fill: ${primary[200]};
  width: 50%;
`;

const SelectedRowsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  height: 100%;
  box-sizing: border-box;
`;

const WithdrawalReceipt = () => {
  const { userAccount } = useAuth();
  const { pathname } = useLocation();
  const isHistory = pathname === "/transaksi/pencairan-bon/riwayat";
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const {
    meta,
    searchText,
    setSearchText,
    params,
    setParams,
    isLoading,

    dataTrxType,
    dataCompanies,
    dataCompanyRelations,
    selectedRows,
    withdrawalList,
    dataCompanyCash,
    handleCheckRow,
    handleCheckAllRows,
    totalReceiptAmount,
    withdrawAmount,
    setWithdrawAmount,
    withdrawDate,
    setWithdrawDate,
    withdrawDesc,
    setWithdrawDesc,
    setModalConfig,
    obsRef,
    handleWithdraw,
  } = useWithdrawalReceipt();
  const cashOnHand = dataCompanyCash ? Number(dataCompanyCash.ending_balance) : undefined;
  const maxCashOnHand = dataCompanyCash?.maximal_cashonhand
    ? Number(dataCompanyCash.maximal_cashonhand.max_coh)
    : undefined;

  const calculateCashOnHand = () => {
    const remainingCashOnHand = cashOnHand !== undefined
      ? cashOnHand - totalReceiptAmount + withdrawAmount
      : undefined;
    return (
      ((remainingCashOnHand !== undefined) && remainingCashOnHand >= 0) ? toRupiah(remainingCashOnHand) : "-"
    );
  };

  const onClickWithdraw = () => {
    if (
      cashOnHand === undefined
      || maxCashOnHand === undefined
      || cashOnHand - totalReceiptAmount + withdrawAmount > maxCashOnHand
    ) {
      snackbar.show(
        `Saldo melebihi batas maksimum ${toRupiah(maxCashOnHand || 0)}. Silakan sesuaikan nominal sesuai kebijakan perusahaan.`,
        "error",
      );
    } else setModalConfig({ type: "CONFIRM_WITHDRAW" });
  };
  const filtersComponent = (
    <ActionSection style={{ justifyContent: "unset" }}>
      <SearchInput
        placeholder="No. pengajuan bon atau Deskripsi"
        onChange={(e) => setSearchText(e.target.value)}
        value={searchText}
        style={{ width: 275 }}
      />
      <FilterDropdown
        id="pool"
        label="Pool"
        width="250px"
        value={params.pool?.label || ""}
        onSelected={(val: IOptionItem) => setParams(
          (curr) => ({
            ...curr,
            areaId: userAccount.position_info.find((item) => item.pool_id === val.value)?.area_id,
            pool: val as { label: string, value: string },
            company: undefined,
          }),
        )}
        options={userAccount.userPools.map((item) => (
          { label: item.poolName, value: item.poolId }
        ))}
      />
      <FilterDropdown
        id="company"
        label="Perusahaan"
        width="150px"
        allowSearch
        value={params.company?.label || ""}
        onSelected={(val: IOptionItem) => setParams(
          (curr) => ({
            ...curr,
            company: val as { label: string, value: string },
            companyRelation: undefined,
          }),
        )}
        options={[...Array.from(new Set(dataCompanies?.lstmstbusiness.map((item) => (
          JSON.stringify({ label: item.company_code, value: item.company_code })
        )))).map((item) => JSON.parse(item)) || []]}
        disable={(!dataCompanies || dataCompanies.lstmstbusiness.length === 0) || undefined}
      />
      <FilterDropdown
        id="company-relation"
        label="Perusahaan relasi"
        width="170px"
        value={params.companyRelation?.label || ""}
        onSelected={(val: IOptionItem) => setParams(
          (curr) => ({
            ...curr,
            companyRelation: val as { label: string, value: string },
          }),
        )}
        options={dataCompanyRelations?.list_mtcompany.map((item) => (
          { label: item.company_friend, value: item.company_friend }
        )) || []}
        disable={!dataCompanyRelations || dataCompanyRelations.list_mtcompany.length <= 1}
      />
    </ActionSection>
  );

  return (<>
    <PageWrapper style={{ display: !isHistory ? "flex" : "none" }} >
      <HeaderText>
        <Typography level={3} color={gray.title} fontWeight="medium">Pencairan bon</Typography>
      </HeaderText>
      <GridWrapper $show={!!selectedRows.length}>
        <PageCard>
          <CardContent style={{ gap: 16, padding: 16 }}>
            <div style={{ display: "flex", gap: 9, justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 9, fontWeight: 500 }}>
                <img src={logo} width={32} height={26} />
                <Typography level={4} fontWeight="medium" color={gray.subtitle}>Daftar pencairan bon</Typography>
              </div>
              <Status content={
                <Typography level={4} fontWeight="medium">Cash on hand : {cashOnHand !== undefined ? <b>{toRupiah(cashOnHand)}</b> : "- (Harap pilih Pool & Perusahaan)"}</Typography>
              } />
            </div>
            <Button
              variant="secondary"
              leftIcon={<PaperListIcon />}
              style={{ width: "fit-content" }}
              onClick={() => navigate("/transaksi/pencairan-bon/riwayat")}
            >Riwayat pencairan</Button>
            {filtersComponent}
            <WithdrawalTable />
          </CardContent>
        </PageCard>
        {!!selectedRows.length
          && <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <PageCard style={{ height: "calc(320px + 32px)" }}>
              <div className="InputPencairanWrap" style={{
                display: "grid",
                gap: 16,
                padding: 16,
              }}>
                <Typography level={4} fontWeight="medium" color={gray.subtitle}>Pencairan</Typography>
                <InputWrapper className="InputPencairan">
                  <FormGroup style={{ gap: 16 }}>
                    <FormWrapper style={{ background: white }} >
                      <DatePicker
                        variant="default"
                        label="Tanggal pencairan"
                        onChange={(date) => setWithdrawDate(getDateIfValid(date))}
                        onReset={() => setWithdrawDate(undefined)}
                        onSubmit={(dates) => {
                          const date = dates?.[0];
                          setWithdrawDate(getDateIfValid(date));
                        }}
                        value={withdrawDate}
                        maxDate={new Date()}
                      />
                    </FormWrapper>
                    <FormWrapper style={{ background: white }} >
                      <NumericFormat
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="Rp "
                        value={withdrawAmount}
                        onChange={(e) => {
                          setWithdrawAmount(e.target.value ? Number(toNumber(e.target.value)) : 0);
                        }}

                        customInput={Form}
                        label="Jumlah pencairan"
                        style={{ width: "50%" }}
                      />
                    </FormWrapper>
                  </FormGroup>
                  <FormGroup>
                    <FormWrapper style={{ background: white }} >
                      <TextArea
                        label="Deskripsi"
                        data-testid="textArea"
                        rows={3}
                        placeholder="Keterangan (Maks. 50 karakter)"
                        onChange={(e) => {
                          const desc = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
                          setWithdrawDesc(desc.slice(0, 50));
                        }}
                        value={withdrawDesc}
                      />
                    </FormWrapper>
                  </FormGroup>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}>
                    <Wrap>
                      <CircledIcon $bgColor={primary[500]} >
                        <MoneyIcon color="white" />
                      </CircledIcon>
                      <div>
                        <Typography level={6} fontWeight="medium" color={primary[500]}>Total cash on hand</Typography>
                        <br />
                        <Typography level={6} fontWeight="bold" color={primary[500]}>
                          {withdrawAmount !== undefined && cashOnHand !== undefined ? toRupiah(cashOnHand + withdrawAmount) : "-"}
                        </Typography>
                      </div>
                    </Wrap>
                    <Wrap>
                      <CircledIcon $bgColor={primary[500]} >
                        <MoneyIcon color="white" />
                      </CircledIcon>
                      <div>
                        <Typography level={6} fontWeight="medium" color={primary[500]}>Total bon</Typography>
                        <br />
                        <Typography level={6} fontWeight="bold" color={primary[500]}>
                          {toRupiah(totalReceiptAmount)}
                        </Typography>
                      </div>
                    </Wrap>
                    <Wrap>
                      <CircledIcon $bgColor={primary[500]} >
                        <MoneyIcon color="white" />
                      </CircledIcon>
                      <div>
                        <Typography level={6} fontWeight="medium" color={primary[500]}>Saldo cash on hand</Typography>
                        <br />
                        <Typography level={6} fontWeight="bold" color={primary[500]}>
                          {calculateCashOnHand()}
                        </Typography>
                      </div>
                    </Wrap>
                  </div>
                </InputWrapper>
                <Button
                  disabled={
                    cashOnHand === undefined
                    || cashOnHand - totalReceiptAmount + withdrawAmount < 0
                    || !withdrawDate
                    || !withdrawDesc
                  }
                  onClick={onClickWithdraw}
                >Cairkan</Button>
              </div>
            </PageCard>
            <PageCard style={{ height: "calc(90vh - (320px + 32px + 16px))" }}>
              <SelectedRowsWrap>
                <Typography level={4} fontWeight="medium" color={gray.subtitle}>Daftar pencairan</Typography>

                <div style={{ overflowY: "auto", minHeight: 100 }}>
                  {selectedRows
                    .map((item) => (
                      <div
                        key={`selected-item-row-${item.trx_id}`}
                        style={{
                          display: "flex", flexDirection: "column", gap: 20, height: "fit-content",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <div style={{ width: "calc(100% - 48px - 16px)" }}>
                            <Typography level={7} color={gray.subtitle}>
                              {toStringDate(item.trx_date)}
                            </Typography>
                            <Typography level={7} color={gray.subtitle} fontWeight="medium"> • No. bon {item.trx_id}</Typography>
                            <br />
                            <Typography level={5} color={primary[500]} fontWeight="bold">Bon : {toRupiah(item.amount)}</Typography>
                            <br />
                            <Typography level={7} color={gray.subtitle} fontWeight="regular">Akun GL : </Typography>
                            <Typography level={7} color={gray.subtitle} fontWeight="bold">
                              <span style={{ display: "inline-flex" }}>
                                {item.trx_code}
                                &nbsp;•&nbsp;
                                {CommonTrxTypes.find(
                                  (type) => type.value === item.sap_shkzg,
                                )?.label}
                                <br />
                                {item.trx_desc}
                              </span>
                            </Typography>
                            <br />
                            <div className="ellipsis-text">
                              <Typography level={7} color={gray.bodyText} fontWeight="regular">{item.trx_text}</Typography>
                            </div>
                          </div>
                          <div>
                            <Button
                              variant="text"
                              onClick={() => handleCheckRow(item)}
                            >
                              <CrossIcon />
                            </Button>
                          </div>
                        </div>
                        <Divider color={primary[200]} />
                      </div>
                    ))}
                </div>

              </SelectedRowsWrap>
            </PageCard>
          </div>
        }
      </GridWrapper>
    </PageWrapper>
    <ModalReceiptDetail />
    {isLoading && (
      <PageLoading />
    )}
  </>);
};

const WithdrawalReceiptProvider = () => {
  const { pathname } = useLocation();
  const isHistory = pathname === "/transaksi/pencairan-bon/riwayat";
  return <Provider>
    <WithdrawalReceipt />
    <WithdrawalReceiptHistory />
    <ModalFilters />
    <ModalWithdrawConfirm />
  </Provider>;
};

export default WithdrawalReceiptProvider;
